import { getLocalCart, setLocalCart } from '@/redux/cart/cart.local';
import { addToCartApi, removeFromCartApi, updateCartItemApi } from '@/services/cart.service';
import { CONSTANTS } from '@/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notify } from 'react-notify-toast';
import { useSelector } from 'react-redux';
import { useRefreshCart } from './useRefreshCart';

export function useAddToCart() {
  const loggedIn = useSelector((s) => s.auth.loggedIn);
  const qc = useQueryClient();
  const { refreshCart } = useRefreshCart();

  return useMutation(
    async (item) => {
      const categoryLabel = CONSTANTS.CATEGORY_DISPLAY_NAMES[item.itemType] || item.itemType;
      const payload = { ...item, category: categoryLabel };

      if (loggedIn) {
        try {
          // 🎯 FIXED: Refresh cart first to ensure we have latest data from server
          console.log('Refreshing cart before adding item:', payload.name || payload.title);
          const freshCartData = await refreshCart();
          const freshItems = freshCartData?.items || [];

          // Check if item already exists in fresh cart data
          const existingItem = freshItems.find((i) => i.id === payload.id);

          if (existingItem) {
            console.log('Item already exists in cart:', payload.name || payload.title);
            return { cart: freshCartData, added: false, item: existingItem };
          }

          console.log('Adding new item to server cart:', payload.name || payload.title);
          const response = await addToCartApi(payload);

          // Refresh cart again to get updated data
          const updatedCartData = await refreshCart();
          return { cart: updatedCartData, added: true, item: payload };
        } catch (error) {
          console.error('Error adding item to server cart:', error);
          throw new Error(error.response?.data?.message || 'Gagal menambahkan item ke keranjang');
        }
      }

      // Local cart case (not logged in)
      const raw = getLocalCart();
      const cart = Array.isArray(raw) ? raw : raw.cart || [];
      const existingItem = cart.find((i) => i.id === payload.id);

      if (existingItem) {
        // Item already exists, don't add again
        console.log('Item already exists in local cart:', payload.name || payload.title);
        return { cart, added: false, item: existingItem };
      } else {
        // Add new item
        const newItem = { ...payload, quantity: 1 };
        cart.push(newItem);
        setLocalCart(cart);
        return { cart, added: true, item: newItem };
      }
    },
    {
      onMutate: async (newItem) => {
        // Cancel any outgoing refetches
        await qc.cancelQueries(['cart']);

        // Snapshot the previous cart
        const previousCart = qc.getQueryData(['cart']);

        // 🎯 FIXED: Skip optimistic update for logged in users
        // because we refresh cart data directly in the mutation
        // This prevents cache inconsistency
        const loggedIn = qc.getQueryData(['auth'])?.loggedIn;

        if (loggedIn) {
          console.log('Skipping optimistic update for logged in user');
          return { previousCart };
        }

        // Only do optimistic update for local cart (guest users)
        qc.setQueryData(['cart'], (old) => {
          if (!old) return { items: [newItem], totalItems: 1, totalQuantity: 1 };

          const items = old.items || [];
          const existingIndex = items.findIndex((item) => item.id === newItem.id);

          if (existingIndex >= 0) {
            // Item already exists, don't modify cart
            return old;
          } else {
            // Add new item
            const newItems = [...items, { ...newItem, quantity: 1 }];
            return {
              ...old,
              items: newItems,
              totalItems: newItems.length,
              totalQuantity: newItems.reduce((sum, item) => sum + (item.quantity || 1), 0),
              isEmpty: false,
            };
          }
        });

        return { previousCart };
      },
      onError: (err, newItem, context) => {
        // Rollback
        if (context?.previousCart) {
          qc.setQueryData(['cart'], context.previousCart);
        }
        notify.show(err.message || 'Gagal menambahkan item ke keranjang', 'error');
      },
      onSuccess: (result) => {
        // 🎯 FIXED: Don't show automatic notifications here
        // Let the calling component handle notifications based on result.added
        console.log('Add to cart result:', {
          added: result.added,
          itemName: result.item?.name || result.item?.title,
        });
      },
      onSettled: () => {
        qc.invalidateQueries(['cart']);
      },
    }
  );
}

export function useUpdateCartItem() {
  const loggedIn = useSelector((s) => s.auth.loggedIn);
  const qc = useQueryClient();
  const { refreshCart } = useRefreshCart();

  return useMutation(
    async ({ id, quantity }) => {
      if (quantity <= 0) {
        throw new Error('Jumlah item harus lebih dari 0');
      }

      if (loggedIn) {
        try {
          await updateCartItemApi({ id, quantity });
          const freshCartItems = await refreshCart();
          return { cart: freshCartItems, updated: true, id, quantity };
        } catch (error) {
          console.error('Error updating cart item:', error);
          throw new Error(error.response?.data?.message || 'Gagal memperbarui jumlah item');
        }
      }

      // Local cart case
      const cart = getLocalCart();
      const updatedCart = cart.map((i) => (i.id === id ? { ...i, quantity } : i));
      setLocalCart(updatedCart);
      return { cart: updatedCart, updated: true, id, quantity };
    },
    {
      onMutate: async ({ id, quantity }) => {
        await qc.cancelQueries(['cart']);
        const previousCart = qc.getQueryData(['cart']);

        // Optimistically update
        qc.setQueryData(['cart'], (old) => {
          if (!old) return old;

          const items = old.items || [];
          const updatedItems = items.map((item) => (item.id === id ? { ...item, quantity } : item));

          return {
            ...old,
            items: updatedItems,
            totalQuantity: updatedItems.reduce((sum, item) => sum + (item.quantity || 1), 0),
          };
        });

        return { previousCart };
      },
      onError: (err, variables, context) => {
        if (context?.previousCart) {
          qc.setQueryData(['cart'], context.previousCart);
        }
        notify.show(err.message || 'Gagal memperbarui jumlah item', 'error');
      },
      onSuccess: () => {
        notify.show('Jumlah item berhasil diperbarui', 'success');
      },
      onSettled: () => {
        qc.invalidateQueries(['cart']);
      },
    }
  );
}

export function useRemoveFromCart() {
  const loggedIn = useSelector((s) => s.auth.loggedIn);
  const qc = useQueryClient();
  const { refreshCart } = useRefreshCart();

  return useMutation(
    async (id) => {
      let itemName = 'Item';

      // Get item name for notification
      const currentCart = qc.getQueryData(['cart']);
      if (currentCart?.items) {
        const item = currentCart.items.find((i) => i.id === id);
        itemName = item?.name || item?.title || 'Item';
      }

      if (loggedIn) {
        try {
          await removeFromCartApi(id);
          const freshCartItems = await refreshCart();
          return { cart: freshCartItems, removed: true, id, itemName };
        } catch (error) {
          console.error('Error removing cart item:', error);
          throw new Error(error.response?.data?.message || 'Gagal menghapus item dari keranjang');
        }
      }

      // Local cart case
      const cart = getLocalCart();
      const filteredCart = cart.filter((i) => i.id !== id);
      setLocalCart(filteredCart);
      return { cart: filteredCart, removed: true, id, itemName };
    },
    {
      onMutate: async (id) => {
        await qc.cancelQueries(['cart']);
        const previousCart = qc.getQueryData(['cart']);

        // Optimistically remove item
        qc.setQueryData(['cart'], (old) => {
          if (!old) return old;

          const items = old.items || [];
          const filteredItems = items.filter((item) => item.id !== id);

          return {
            ...old,
            items: filteredItems,
            totalItems: filteredItems.length,
            totalQuantity: filteredItems.reduce((sum, item) => sum + (item.quantity || 1), 0),
            isEmpty: filteredItems.length === 0,
          };
        });

        return { previousCart };
      },
      onError: (err, id, context) => {
        if (context?.previousCart) {
          qc.setQueryData(['cart'], context.previousCart);
        }
        notify.show(err.message || 'Gagal menghapus item dari keranjang', 'error');
      },
      onSuccess: (result) => {
        notify.show(`${result.itemName} berhasil dihapus dari keranjang`, 'success');
      },
      onSettled: () => {
        qc.invalidateQueries(['cart']);
      },
    }
  );
}

// Bulk operations
export function useClearCart() {
  const loggedIn = useSelector((s) => s.auth.loggedIn);
  const qc = useQueryClient();

  return useMutation(
    async () => {
      if (loggedIn) {
        // If there's a clear cart API endpoint, use it
        // For now, we'll simulate by removing all items
        const currentCart = qc.getQueryData(['cart']);
        if (currentCart?.items) {
          // Remove each item (this might be inefficient for large carts)
          // Ideally there should be a bulk clear API
          const promises = currentCart.items.map((item) => removeFromCartApi(item.id));
          await Promise.all(promises);
        }
      }

      // Clear local storage
      setLocalCart([]);
      return { cleared: true };
    },
    {
      onMutate: async () => {
        await qc.cancelQueries(['cart']);
        const previousCart = qc.getQueryData(['cart']);

        // Optimistically clear cart
        qc.setQueryData(['cart'], {
          items: [],
          totalItems: 0,
          totalQuantity: 0,
          isEmpty: true,
        });

        return { previousCart };
      },
      onError: (err, variables, context) => {
        if (context?.previousCart) {
          qc.setQueryData(['cart'], context.previousCart);
        }
        notify.show('Gagal mengosongkan keranjang', 'error');
      },
      onSuccess: () => {
        notify.show('Keranjang berhasil dikosongkan', 'success');
      },
      onSettled: () => {
        qc.invalidateQueries(['cart']);
      },
    }
  );
}
